'use client';

import { produce } from 'immer';
import { FC, ReactNode } from 'react';

import { FilterOption, FilterParams } from '@/plp/components/PlpFilters/PlpFilters.type';

import { storeFactory } from './storeFactory';

type ResultsCount = number | null;

type PlpStoreInitialState = Pick<PlpStoreStateData, 'totalResults' | 'currentFilters' | 'filterParams' | 'resultUrl'>;

interface PlpStoreStateData {
  currentFilters: FilterOption[];
  totalResults: ResultsCount;
  isLoadingResults: boolean;
  resultUrl: string;
  filterParams: FilterParams;
}

interface PlpStoreStateFunctions {
  setResultUrl: (url: string) => void;
  setCurrentFilters: (currentFilters: FilterOption[]) => void;
  setTotalResults: (totalResults: ResultsCount) => void;
  setIsLoadingResults: (value: boolean) => void;
}

const {
  store: plpStore,
  StoreProvider: PlpStoreProvider,
  useCreateStore: useCreatePlpStore,
} = storeFactory<PlpStoreStateData, PlpStoreStateFunctions>(
  (payload?: PlpStoreInitialState) => ({
    totalResults: payload?.totalResults ?? null,
    currentFilters: payload?.currentFilters ?? [],
    isLoadingResults: false,
    resultUrl: payload?.resultUrl ?? '/used-cars',
    filterParams: payload?.filterParams ?? { filter: '' },
  }),
  (set) => ({
    setCurrentFilters: (currentFilters: FilterOption[]) => {
      set((state) =>
        produce(state, (draft) => {
          draft.currentFilters = currentFilters;
        }),
      );
    },
    setIsLoadingResults: (value: boolean) => {
      set((state) =>
        produce(state, (draft) => {
          draft.isLoadingResults = value;
        }),
      );
    },
    setResultUrl: (url: string) => {
      set((state) =>
        produce(state, (draft) => {
          draft.resultUrl = url;
        }),
      );
    },
    setTotalResults: (totalResults: ResultsCount) => {
      set((state) =>
        produce(state, (draft) => {
          draft.totalResults = totalResults;
        }),
      );
    },
  }),
);

export { plpStore };

export const PlpStore: FC<{
  children: ReactNode;
  payload?: PlpStoreInitialState;
}> = ({ children, payload }) => {
  const createPlpStore = useCreatePlpStore(payload ?? {});
  return <PlpStoreProvider createStore={createPlpStore}>{children}</PlpStoreProvider>;
};
